import React, { useState, useEffect } from 'react';
import { useTheme, createUseStyles } from 'react-jss';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '@mdi/react';
import { mdiClose, mdiChevronRight } from '@mdi/js';
import * as R from 'ramda';

import Container from '@components/Container';
import TracCarousel from './TracCarousel';
import Loading from '@components/Base/Loading';
// import PlaneLine from '@components/Base/PlaneLine';
import PlaneGPS from '@components/Base/PlaneGPS';

import { unLockScroll } from '@utils/ui';
import { fetch } from '@utils/fetch';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Shanghai');

const useStyles = createUseStyles(({
  fontColor,
  rem,
}) => ({
  '@global': {
    button: {
      outline: 'transparent',
      margin: rem(6),
    },
  },
  root: {
    width: '100vw',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    color: '#000',
    zIndex: 999,
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
  child: {
    position: 'relative',
    width: '100%',
    overflowY: 'auto',
  },
  close: {
    width: 24,
    height: 24,
    outline: 'transparent',
    border: 'unset',
    backgroundColor: 'transparent',
    color: fontColor.primary,
    position: 'absolute',
    top: 10,
    right: 20,
    cursor: 'pointer',
    padding: 0,
  },
  baseInfo: {
    width: '100%',
    // height: '100%',
    maxWidth: 480,
    marginTop: rem(50),
    textAlign: 'center',
    '& > p': {
      fontSize: rem(12),
      margin: [rem(8), 0],
      maxWidth: rem(255),
      lineHeight: rem(18),
      color: '#595959',
    }
  },
  title: {
    fontSize: '18px !important',
    color: '#262626 !important',
    fontWeight: 'bold !important',
    lineHeight: '25px !important',
  },
  details: {
    width: '90%',
    borderRadius: 3,
    margin: [rem(24), 'auto'],
    border: '1px solid rgba(13, 38, 91, 0.08)',
    boxShadow: '0px 1px 1px -1px rgba(13, 38, 91, 0.08), 0px 3px 3px 0px rgba(13, 38, 91, 0.06), 0px 5px 6px 2px rgba(13, 38, 91, 0.04)',
  },
  detailsLine: {
    padding: rem(24),
    '& > p': {
      margin: 0,
    }
  },
  line: {
    display: 'flex',
    fontSize: rem(12),
    marginBottom: '16px !important',
    '& > span:first-child': {
      width: rem(80),
      display: 'inline-block',
      color: 'rgba(140, 140, 140, 1)',
    },
    '& > span:last-child': {
      marginLeft: rem(10),
      maxWidth: rem(200),
      color: 'rgba(0, 0, 0, 1)',
      lineHeight: rem(18),
      wordBreak: 'break-all',
    },
    '&:last-child': {
      marginBottom: '0px !important',
    }
  },
  aLink: {
    color: '#57BCF9 !important',
    '& > a': {
      textDecoration: 'none',
    }
  },
  block: {
    margin: [rem(10), 'auto'],
    fontSize: rem(14),
    width: '90%',
    color: '#57BCF9',
    borderRadius: 3,
    border: '1px solid rgba(13, 38, 91, 0.08)',
    boxShadow: '0px 1px 1px -1px rgba(13, 38, 91, 0.08), 0px 3px 3px 0px rgba(13, 38, 91, 0.06), 0px 5px 6px 2px rgba(13, 38, 91, 0.04)',
    '& > div':{
      padding: [rem(6), rem(12)],
    },
    '& > p': {
      fontSize: rem(12),
      margin: [rem(6), rem(12)],
    },
  },
  blockID: {
    fontSize: rem(12),
    color: '#595959',
    border: '1px solid #F8F9FA',
    padding: rem(5),
    margin: [0, rem(12)],
    backgroundColor: '#F8F9FA',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    wordBreak: 'break-all',
  },
  tips: {
    fontSize: rem(12),
    color: '#BFBFBF',
    width: '90%',
    lineHeight: rem(18),
    margin: [rem(30), 'auto'],
  },
  loading: {
    color: '#000',
    margin: [rem(200), 0],
  },
  plane: {
    height: rem(150),
    width: '100%',
  },
}), {
    name: 'TrancSourceModal'
});

const TypeTitle = t => {
  return {
    'dcp1': {
      name: t('procurement'),
      icon: './static/images/products/dcp1.png',
    },
    'dcp2': {
      name: t('yarn'),
      icon: './static/images/products/dcp2.png',
    },
    'dcp3': {
      name: t('weav'),
      icon: './static/images/products/dcp3.png',
    },
    'dcp4': {
      name: t('garment'),
      icon: './static/images/products/dcp4.png',
    },
    'trackinfo': {
      name: t('shipment'),
      icon: './static/images/products/trackinfo.png',
    }
  }
}

const TrancSourceModal = props => {
  const {
    t,
    data,
    language,
    trackinfo,
    setOpenTrancSource,
  } = props;

  const theme = useTheme();
  const classes = useStyles(theme);
  const [loading, setLoading] = useState(false);
  const [currData, setCurrData] = useState(null);
  const [url, setUrl] = useState('');

  const belong = R.pathOr('dcp1', ['belong'])(data);
  const index = R.pathOr('0', ['index'])(data);

  useEffect(() => {
    setLoading(true);
    const params = {
      types: [belong],
      language,
    };
    const getTranc = async () => {
      await fetch(params).then(data => {
        const rst = R.pathOr({}, ['jsonData', belong, index])(data);
        setCurrData(rst);
        const explorerUrl = R.pathOr('', ['explorerUrl'])(data);
        setUrl(explorerUrl);
        setLoading(false);
      }, err => {
        console.log(err, '请求数据失败，请重新尝试');
      })
    }
    getTranc();
  }, [belong, index, language]);

  const time = R.pathOr(new Date().getTime(), ['base', 'timestamp'])(currData);
  const country = R.pathOr(t('noData'), ['custom', 'gps', 'country'])(currData);
  const city = R.pathOr(t('noData'), ['custom', 'gps', 'city'])(currData);

  const handleCloseShare = () => {
    setOpenTrancSource(false);
    unLockScroll();
  }

  // https://explore.vechain.org/transactions/{id}
  const handleGotoChain = (id) => {
    // unLockScroll(); // 页面跳转前先取消锁定
    global.location.href = `${url}/transactions/${id}`;
  }

  const createDetailsInfo = () => {
    const vendor = R.pathOr(t('noData'), ['custom', 'vendor'])(currData);
    const vendorcontact = R.pathOr(t('noData'), ['custom', 'vendorcontact'])(currData);
    const contactNumber = R.pathOr(t('noData'), ['custom', 'vendorcontactnumber'])(currData);
    const email = R.pathOr(t('noData'), ['custom', 'vendoremail'])(currData);
    const website = R.pathOr(t('noData'), ['custom', 'vendorwebsite'])(currData);
    const info = R.pathOr(t('noData'), ['custom', 'vendorinfo'])(currData);
    const purchaseDate = R.pathOr(new Date().getTime(), ['custom', 'purchasedate'])(currData);

    return (
      <Container
        flex
        direction='column'
        className={classes.detailsLine}
      >
        <p className={classes.line}>
          <span>{t('mf_name')}</span>
          <span>{vendor}</span>
        </p>
        <p className={classes.line}>
          <span>{t('charge')}</span>
          <span>{vendorcontact}</span>
        </p>
        <p className={classes.line}>
          <span>{t('mf_website')}</span>
          <span className={classes.aLink}>
            <a href={website} rel='noreferrer'>
              {website}
            </a>
          </span>
        </p>
        <p className={classes.line}>
          <span>{t('manufacturer')}</span>
          <span>{info}</span>
        </p>
        <p className={classes.line}>
          <span>{t('email')}</span>
          <span>{email}</span>
        </p>
        <p className={classes.line}>
          <span>{t('contact')}</span>
          <span>{contactNumber}</span>
        </p>
        <p className={classes.line}>
          <span>{t('origon')}</span>
          <span>{`${country} ${city}`}</span>
        </p>
        {/* <p className={classes.line}>
          <span>品种</span>
          <span>有机棉花</span>
        </p> */}
        <p className={classes.line}>
          <span>{t('purchase_time')}</span>
          <span>{dayjs.tz(purchaseDate).format('YYYY-MM-DD')}</span>
        </p>
      </Container>
    )
  };

  // 出货详情
  const createTrackinfoDetails = () => {
    const department = R.pathOr(t('noData'), ['custom', 'department'])(currData);
    const moreinfolist = R.pathOr([], ['custom', 'moreinfolist'])(currData);

    return (
      <Container
        flex
        direction='column'
        className={classes.detailsLine}
      >
        <p className={classes.line}>
          <span>{t('department')}</span>
          <span>{department}</span>
        </p>
        <>
        {
          (moreinfolist && moreinfolist.length > 0) && (
            moreinfolist.map((item, index) => (
              <p className={classes.line} key={String(index)}>
                <span>{R.pathOr('', ['moreinfokey'])(item)}</span>
                <span>{R.pathOr('', ['moreinfovalue'])(item)}</span>
              </p>
            ))
          )
        }
        </>
        <p className={classes.line}>
          <span>{t('time')}</span>
          <span>{dayjs.tz(time).format('YYYY-MM-DD')}</span>
        </p>
      </Container>
    )
  }

  const createBasicSpecial = () => {
    const description = R.pathOr(t('noData'), ['custom', 'description'])(currData);
    return (
      <p className={classes.desc}>{description}</p>
    );
  };

  const createBasicInfo = () => {
    return (
      <Container
        flex
        direction='column'
        alignItems='center'
        className={classNames(classes.baseInfo)}
      >
        <img
          src={TypeTitle(t)[belong].icon}
          alt='logo'
          height={65}
          width={65}
        />
        <p className={classes.title}>{TypeTitle(t)[belong].name}</p>
        <p>{`${country} ${city}`}</p>
        <p>{dayjs.tz(time).format('YYYY-MM-DD HH:mm:ss')}</p>
        {
          (belong !== 'trackinfo') && createBasicSpecial()
        }
      </Container>
    )
  };

  const createBlock = () => {
    const txId = R.pathOr('', ['blockinfo', 'txId'])(currData);
    return (
      <Container
        flex
        direction='column'
        className={classes.block}
        onClick={() => handleGotoChain(txId)}
      >
        <Container
          flex
          justify='space-between'
          alignItems='center'
        >
          <span>{t('block_rds')}</span>
          <Icon path={mdiChevronRight} size='24px' />
        </Container>
        <span className={classes.blockID}>{txId}</span>
        <p>
          POWERED BY
          <img src="./static/images/logo_vechain.png" alt="logo" height={12} style={{ marginLeft: 10 }} />
        </p>
      </Container>
    )
  }

  const createMain = () => {
    return (
      <Container
        className={classes.child}
      >
        <button
          onClick={handleCloseShare}
          className={classNames(classes.close)}
        >
          <Icon path={mdiClose} size='24px' />
        </button>

        {createBasicInfo()}

        <Container
          flex
          justify='center'
          alignItems='flex-start'
          direction='column'
          className={classNames(classes.details)}
        >
          {belong === 'trackinfo' ? createTrackinfoDetails() : createDetailsInfo()}

          {(!loading && belong === 'trackinfo') && (
            <PlaneGPS
              data={data}
              trackinfo={trackinfo}
              className={classes.plane}
            />
          )}

          {
            !loading && (
              <TracCarousel
                t={t}
                belong={data}
                data={currData}
              />
            )
          }
        </Container>

        {createBlock()}

        <p className={classes.tips}>
          {t('only_code')}
        </p>
      </Container>
    )
  }

  return (
    <Container
      flex
      direction='column'
      alignItems='center'
      className={classNames(classes.root)}
    >
      {
        loading ? (<Loading size={1} className={classes.loading} />) : createMain()
      }
    </Container>
  )
};

TrancSourceModal.propTypes = {
  t: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  setOpenTrancSource: PropTypes.func.isRequired,
  trackinfo: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]).isRequired,
}

export default TrancSourceModal;
